/**
 * Logger abstraction for Adhesion game
 * Provides structured logging with different levels
 * Can be configured for different environments
 */
export var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["DEBUG"] = 0] = "DEBUG";
    LogLevel[LogLevel["INFO"] = 1] = "INFO";
    LogLevel[LogLevel["WARN"] = 2] = "WARN";
    LogLevel[LogLevel["ERROR"] = 3] = "ERROR";
    LogLevel[LogLevel["NONE"] = 4] = "NONE";
})(LogLevel || (LogLevel = {}));
class Logger {
    static instance;
    config;
    logs = [];
    constructor() {
        // Check if we're in Node.js or browser environment
        const isProduction = typeof process !== 'undefined'
            ? process.env['NODE_ENV'] === 'production'
            : false; // In browser, default to development mode
        this.config = {
            level: isProduction ? LogLevel.WARN : LogLevel.DEBUG,
            enableConsole: !isProduction,
            enableFile: false,
            maxLogSize: 1000,
        };
    }
    static getInstance() {
        if (!Logger.instance) {
            Logger.instance = new Logger();
        }
        return Logger.instance;
    }
    configure(config) {
        this.config = { ...this.config, ...config };
    }
    shouldLog(level) {
        return level >= this.config.level;
    }
    formatMessage(entry) {
        const timestamp = entry.timestamp.toISOString();
        const level = LogLevel[entry.level];
        const context = entry.context ? JSON.stringify(entry.context) : '';
        return `[${timestamp}] [${level}] ${entry.message} ${context}`.trim();
    }
    log(level, message, context, error) {
        if (!this.shouldLog(level))
            return;
        const entry = {
            level,
            message,
            context: context || undefined,
            timestamp: new Date(),
            error: error || undefined,
        };
        // Store in memory (circular buffer)
        this.logs.push(entry);
        if (this.logs.length > this.config.maxLogSize) {
            this.logs.shift();
        }
        // Output to console in development
        if (this.config.enableConsole) {
            const formattedMessage = this.formatMessage(entry);
            switch (level) {
                case LogLevel.ERROR:
                    // eslint-disable-next-line no-console
                    console.error(formattedMessage, error);
                    break;
                case LogLevel.WARN:
                    // eslint-disable-next-line no-console
                    console.warn(formattedMessage);
                    break;
                case LogLevel.INFO:
                case LogLevel.DEBUG:
                default:
                    // In production, these are silent
                    const isProduction = typeof process !== 'undefined'
                        ? process.env['NODE_ENV'] === 'production'
                        : false;
                    if (!isProduction) {
                        // eslint-disable-next-line no-console
                        console.log(formattedMessage);
                    }
                    break;
            }
        }
    }
    debug(message, context) {
        this.log(LogLevel.DEBUG, message, context);
    }
    info(message, context) {
        this.log(LogLevel.INFO, message, context);
    }
    warn(message, context) {
        this.log(LogLevel.WARN, message, context);
    }
    error(message, error, context) {
        this.log(LogLevel.ERROR, message, context, error);
    }
    getRecentLogs(count = 100) {
        return this.logs.slice(-count);
    }
    clearLogs() {
        this.logs = [];
    }
}
// Export singleton instance
export const logger = Logger.getInstance();
// Export default for convenience
export default logger;
//# sourceMappingURL=Logger.js.map